/**
 * /dashboard/admin/health — System monitoring dashboard.
 *
 * Read-only view of the `systemHealth/*` docs written by the
 * `systemHealthCheck` Cloud Function (every 15 min) and by the OpenAI
 * hot-path instrumentation (functions/health/). The underlying alerts go to
 * admin email; this page is the at-a-glance status board.
 *
 * Heartbeat expectations are mirrored from functions/health/heartbeats.js —
 * keep the two in sync when scheduled functions are added/removed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { RefreshCw, Activity, Database, Cpu, DollarSign } from 'lucide-react';
import { db } from '../../services/firebase/config';

type HealthStatus = 'ok' | 'warn' | 'down' | 'unknown';

/** Mirror of functions/health/heartbeats.js (keep in sync). */
const HEARTBEAT_EXPECTATIONS: { name: string; intervalMs: number; label: string }[] = [
  { name: 'processEmailQueue', intervalMs: 15 * 60 * 1000, label: 'Email queue (every 15 min)' },
  { name: 'processScheduledEmails', intervalMs: 5 * 60 * 1000, label: 'Scheduled emails (every 5 min)' },
  { name: 'processIncompleteRegistrations', intervalMs: 60 * 60 * 1000, label: 'Incomplete registrations (hourly)' },
  { name: 'publishScheduledBlogPosts', intervalMs: 60 * 60 * 1000, label: 'Publish scheduled blog posts (hourly)' },
  { name: 'cleanupAnonymousUsers', intervalMs: 24 * 60 * 60 * 1000, label: 'Anonymous user cleanup (daily, 03:00 UTC)' },
  { name: 'generateWeeklyContentReport', intervalMs: 7 * 24 * 60 * 60 * 1000, label: 'Weekly content report (Mon 09:00 UTC)' },
  { name: 'systemHealthCheck', intervalMs: 15 * 60 * 1000, label: 'System health check (every 15 min)' },
];

interface SpendDay {
  inputTokens: number;
  outputTokens: number;
  embeddingTokens: number;
  estimatedUsdCents: number;
  callCount: number;
}

interface HealthData {
  summary: any | null;
  openai: any | null;
  spend: any | null;
  probe: any | null;
  beats: Record<string, Date | null>;
  firstObserved: Record<string, Date | null>;
  error: string | null;
}

function toDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function utcDayKey(d = new Date()): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function formatInterval(ms: number): string {
  const min = 60 * 1000;
  const hour = 60 * min;
  const day = 24 * hour;
  const week = 7 * day;
  if (ms >= week) return `${Math.round(ms / week)}w`;
  if (ms >= day) return `${Math.round(ms / day)}d`;
  if (ms >= hour) return `${Math.round(ms / hour)}h`;
  return `${Math.round(ms / min)}m`;
}

const STATUS_STYLES: Record<HealthStatus, { bg: string; text: string; label: string }> = {
  ok: { bg: 'bg-green-100', text: 'text-green-700', label: 'OK' },
  warn: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'WARN' },
  down: { bg: 'bg-red-100', text: 'text-red-700', label: 'DOWN' },
  unknown: { bg: 'bg-neutral-100', text: 'text-neutral-500', label: 'UNKNOWN' },
};

function StatusPill({ status, size = 'sm' }: { status: HealthStatus; size?: 'sm' | 'lg' }) {
  const c = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  const pad = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full font-semibold ${pad} ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

const SystemHealthPage: React.FC = () => {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const col = (id: string) => getDoc(doc(db, 'systemHealth', id));
      const [summarySnap, openaiSnap, spendSnap, probeSnap, beatsSnap] = await Promise.all([
        col('summary'),
        col('openai'),
        col('openaiSpend'),
        col('firestoreProbe'),
        col('heartbeats'),
      ]);
      const beatsDoc: any = beatsSnap.exists() ? beatsSnap.data() : null;
      const beats: Record<string, Date | null> = {};
      const firstObserved: Record<string, Date | null> = {};
      for (const [name, value] of Object.entries(beatsDoc?.beats || {})) beats[name] = toDate(value);
      for (const [name, value] of Object.entries(beatsDoc?.firstObserved || {})) firstObserved[name] = toDate(value);
      setData({
        summary: summarySnap.exists() ? summarySnap.data() : null,
        openai: openaiSnap.exists() ? openaiSnap.data() : null,
        spend: spendSnap.exists() ? spendSnap.data() : null,
        probe: probeSnap.exists() ? probeSnap.data() : null,
        beats,
        firstObserved,
        error: null,
      });
    } catch (err: any) {
      setData({
        summary: null, openai: null, spend: null, probe: null,
        beats: {}, firstObserved: {},
        error: err?.message || 'Failed to load system health',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const overall: HealthStatus = data?.summary?.overall || 'unknown';
  const generatedAt = toDate(data?.summary?.generatedAt);
  const staleNames: string[] = data?.summary?.staleHeartbeats || [];

  const components: { key: string; label: string; status: HealthStatus; icon: React.ReactNode }[] = [
    { key: 'openai', label: 'OpenAI', status: data?.summary?.components?.openai || 'unknown', icon: <Cpu className="w-4 h-4" /> },
    { key: 'openaiSpend', label: 'OpenAI spend', status: data?.summary?.components?.openaiSpend || 'unknown', icon: <DollarSign className="w-4 h-4" /> },
    { key: 'heartbeats', label: 'Scheduled functions', status: data?.summary?.components?.heartbeats || 'unknown', icon: <Activity className="w-4 h-4" /> },
    { key: 'firestoreProbe', label: 'Firestore', status: data?.summary?.components?.firestoreProbe || 'unknown', icon: <Database className="w-4 h-4" /> },
  ];

  const spendDays: [string, SpendDay][] = Object.entries(data?.spend?.days || {})
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .slice(0, 30) as [string, SpendDay][];
  const today = utcDayKey();

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">System health</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Live status of OpenAI, Firestore, scheduled functions, and daily spend. Updated by{' '}
            <code className="text-xs bg-neutral-100 px-1 rounded">systemHealthCheck</code> every 15 minutes.
            Public liveness: <code className="text-xs bg-neutral-100 px-1 rounded">/healthz</code>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={overall} size="lg" />
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      {data?.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          Failed to load system health: {data.error}
        </div>
      )}

      {/* Component grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {components.map((c) => (
          <div key={c.key} className="rounded-xl border border-neutral-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-neutral-600">{c.icon}{c.label}</span>
              <StatusPill status={c.status} />
            </div>
          </div>
        ))}
      </div>

      {/* OpenAI + Firestore */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="font-semibold text-neutral-900">OpenAI</h2>
          <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-neutral-500">Status</dt>
            <dd><StatusPill status={data?.openai?.status || 'unknown'} /></dd>
            <dt className="text-neutral-500">Last success</dt>
            <dd className="text-neutral-800">{toDate(data?.openai?.lastSuccessAt)?.toLocaleString() || '—'}</dd>
            <dt className="text-neutral-500">Last error</dt>
            <dd className="text-neutral-800">{toDate(data?.openai?.lastErrorAt)?.toLocaleString() || '—'}</dd>
            <dt className="text-neutral-500">Last error class</dt>
            <dd className="text-neutral-800">{data?.openai?.lastErrorClass || '—'}</dd>
            <dt className="text-neutral-500">Last alert sent</dt>
            <dd className="text-neutral-800">{toDate(data?.openai?.lastAlertedAt)?.toLocaleString() || 'never'}</dd>
          </dl>
          {data?.openai?.lastErrorMessage && (
            <pre className="mt-3 text-xs bg-neutral-50 border border-neutral-200 rounded p-3 text-neutral-600 overflow-auto">
              {data.openai.lastErrorMessage}
            </pre>
          )}
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <h2 className="font-semibold text-neutral-900">Firestore probe</h2>
          <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-neutral-500">Status</dt>
            <dd><StatusPill status={data?.probe?.status || 'unknown'} /></dd>
            <dt className="text-neutral-500">Last success</dt>
            <dd className="text-neutral-800">{toDate(data?.probe?.lastSuccessAt)?.toLocaleString() || '—'}</dd>
            <dt className="text-neutral-500">Last failure</dt>
            <dd className="text-neutral-800">{toDate(data?.probe?.lastFailureAt)?.toLocaleString() || '—'}</dd>
          </dl>
          {data?.probe?.lastFailureMessage && (
            <pre className="mt-3 text-xs bg-neutral-50 border border-neutral-200 rounded p-3 text-neutral-600 overflow-auto">
              {data.probe.lastFailureMessage}
            </pre>
          )}
        </div>
      </section>

      {/* Spend */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-neutral-900">OpenAI spend (last 30 days)</h2>
          {data?.spend?.lastAlertedDay && (
            <span className="text-xs text-neutral-500">Last alert: {data.spend.lastAlertedDay}</span>
          )}
        </div>
        {spendDays.length === 0 ? (
          <p className="text-sm text-neutral-500 mt-3">No spend recorded yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-4">Date (UTC)</th>
                  <th className="py-2 pr-4 text-right">USD</th>
                  <th className="py-2 pr-4 text-right">Input</th>
                  <th className="py-2 pr-4 text-right">Output</th>
                  <th className="py-2 pr-4 text-right">Embedding</th>
                  <th className="py-2 pr-4 text-right">Calls</th>
                </tr>
              </thead>
              <tbody>
                {spendDays.map(([day, b]) => {
                  const isToday = day === today;
                  return (
                    <tr key={day} className={`border-b border-neutral-100 ${isToday ? 'bg-primary-50' : ''}`}>
                      <td className="py-2 pr-4 font-mono text-xs text-neutral-800">
                        {day}{isToday && <span className="ml-1 text-primary-600">·today</span>}
                      </td>
                      <td className="py-2 pr-4 text-right text-neutral-800">${(b.estimatedUsdCents / 100).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right text-neutral-500">{(b.inputTokens || 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right text-neutral-500">{(b.outputTokens || 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right text-neutral-500">{(b.embeddingTokens || 0).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right text-neutral-500">{(b.callCount || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Heartbeats */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="font-semibold text-neutral-900">Scheduled functions</h2>
        <p className="text-xs text-neutral-500 mt-1">
          Each function records a heartbeat when it runs. Status is computed by{' '}
          <code className="text-xs bg-neutral-100 px-1 rounded">systemHealthCheck</code> using each function&apos;s
          cron schedule — a missed run surfaces as DOWN within ~15 minutes. Newly-deployed functions show PENDING
          until their grace window elapses.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 border-b border-neutral-200">
                <th className="py-2 pr-4">Function</th>
                <th className="py-2 pr-4">Every</th>
                <th className="py-2 pr-4">Last beat</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {HEARTBEAT_EXPECTATIONS.map((exp) => {
                const lastBeat = data?.beats?.[exp.name] || null;
                const observed = data?.firstObserved?.[exp.name] || null;
                const stale = staleNames.includes(exp.name);
                const pending = !stale && !lastBeat && !!observed;
                const status: HealthStatus = stale ? (lastBeat ? 'down' : 'warn') : 'ok';
                return (
                  <tr key={exp.name} className="border-b border-neutral-100">
                    <td className="py-2 pr-4">
                      <div className="text-neutral-800">{exp.label}</div>
                      <div className="text-xs text-neutral-400 font-mono">{exp.name}</div>
                    </td>
                    <td className="py-2 pr-4 text-neutral-500">{formatInterval(exp.intervalMs)}</td>
                    <td className="py-2 pr-4 text-neutral-500">
                      {lastBeat
                        ? lastBeat.toLocaleString()
                        : pending
                          ? `awaiting first run (since ${observed?.toLocaleString() || '—'})`
                          : 'never seen'}
                    </td>
                    <td className="py-2 pr-4">
                      {pending ? (
                        <span className="inline-flex items-center rounded-full font-semibold px-2 py-0.5 text-xs bg-neutral-100 text-neutral-500">
                          PENDING
                        </span>
                      ) : (
                        <StatusPill status={status} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="text-xs text-neutral-400 pt-4 border-t border-neutral-100">
        Summary generated at: {generatedAt ? generatedAt.toLocaleString() : "never (systemHealthCheck hasn't run yet)"}
      </footer>
    </div>
  );
};

export default SystemHealthPage;
